import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type?: 'organization' | 'localBusiness' | 'service' | 'breadcrumb' | 'serviceCatalog';
  data?: Record<string, any>;
}

const baseOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Security Engineers, Inc.',
  alternateName: 'Security Engineers',
  url: 'https://www.securityengineers.com',
  logo: 'https://www.securityengineers.com/logo.png',
  description: 'Professional security system installation, maintenance, and 24/7 monitoring services in the San Francisco Bay Area since 1986.',
  foundingDate: '1986',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'US',
    addressRegion: 'CA',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+1-800-540-2140',
    contactType: 'Customer Service',
    areaServed: 'US',
    availableLanguage: 'English',
  },
  sameAs: [
    // Add social media URLs when available
  ],
};

const localBusiness = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': 'https://www.securityengineers.com',
  name: 'Security Engineers, Inc.',
  image: 'https://www.securityengineers.com/logo.png',
  url: 'https://www.securityengineers.com',
  telephone: '+1-800-540-2140',
  priceRange: '$$',
  address: [
    {
      '@type': 'PostalAddress',
      streetAddress: '595 Pacific Avenue',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94133',
      addressCountry: 'US',
    },
    {
      '@type': 'PostalAddress',
      streetAddress: '1457 SOS Drive',
      addressLocality: 'Walnut Creek',
      addressRegion: 'CA',
      postalCode: '94597',
      addressCountry: 'US',
    },
  ],
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '37.7749',
    longitude: '-122.4194',
  },
  areaServed: [
    {
      '@type': 'City',
      name: 'San Francisco',
    },
    {
      '@type': 'City',
      name: 'Oakland',
    },
    {
      '@type': 'City',
      name: 'Berkeley',
    },
    {
      '@type': 'City',
      name: 'Hayward',
    },
    {
      '@type': 'City',
      name: 'Richmond',
    },
    {
      '@type': 'City',
      name: 'Walnut Creek',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Security Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Fire Alarm Systems',
          description: 'Professional fire alarm system installation, maintenance, and monitoring',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Burglar Alarm Systems',
          description: 'Intrusion detection and burglar alarm systems',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'CCTV Systems',
          description: 'Video surveillance and CCTV installation',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Access Control',
          description: 'Access control and entry management systems',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: '24/7 Monitoring',
          description: 'Round-the-clock security monitoring services',
        },
      },
    ],
  },
};

export function StructuredData({ type = 'organization', data }: StructuredDataProps) {
  let schemaData;

  switch (type) {
    case 'organization':
      schemaData = baseOrganization;
      break;
    case 'localBusiness':
      schemaData = localBusiness;
      break;
    case 'service':
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        ...data,
      };
      break;
    case 'breadcrumb':
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data?.items || [],
      };
      break;
    case 'serviceCatalog':
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Security Services',
        provider: baseOrganization,
        areaServed: localBusiness.areaServed,
        hasOfferCatalog: localBusiness.hasOfferCatalog,
        ...data,
      };
      break;
    default:
      schemaData = baseOrganization;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schemaData, null, 2)}</script>
    </Helmet>
  );
}
